import { Attachment } from "@/api/attachment";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Multipleprint } from "@/lib/types";
import { Cross2Icon } from "@radix-ui/react-icons";
import { ReactNode, useEffect, useState } from "react";

function getAttachmentUrl(attachment: Attachment): string | null {
  return (
    attachment.largePublicUrl ??
    attachment.mediumPublicUrl ??
    attachment.publicUrl ??
    null
  );
}

interface MultipleprintFullscreenViewerProps {
  open: boolean;
  onClose: () => void;
  attachment: Attachment;
  data: Multipleprint;
  locale: string;
  t: (key: string) => string;
  dimensionsSummaryCalculated: string;
  supportTechniqueCalculated: string;
  /** Affiché sous les infos (ex. bouton « Voir la fiche » depuis un portfolio). */
  footer?: ReactNode;
  /** Ouvert par-dessus une autre modale. */
  stacked?: boolean;
}

export function MultipleprintFullscreenViewer({
  open,
  onClose,
  attachment,
  data,
  locale,
  t,
  dimensionsSummaryCalculated,
  supportTechniqueCalculated,
  footer,
  stacked = false,
}: MultipleprintFullscreenViewerProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!attachment || !open) return;
    setImageUrl(getAttachmentUrl(attachment));
  }, [attachment, open]);

  const imageAlt =
    (attachment.caption as Record<string, string> | undefined)?.[locale] ??
    (attachment.textAlt as Record<string, string> | undefined)?.[locale] ??
    data.estampesMultiplesDescriptionTitre ??
    "";

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        needCloseButton={false}
        overlay="full"
        preventOutsideClose={false}
        stacked={stacked}
        className="bg-transparent border-none shadow-none h-[95dvh] min-w-[90dvw] flex flex-row justify-center gap-y-2 outline-none"
        onInteractOutside={onClose}
        onEscapeKeyDown={onClose}
      >
        <div className="flex flex-row w-full h-full gap-4">
          {/* Left: image, 3/4 width */}
          <div className="w-full flex items-center justify-center min-w-0 relative">
            {imageUrl && (
              <div className="relative w-full h-full flex items-center justify-center">
                <img
                  src={imageUrl}
                  alt={imageAlt}
                  className="object-contain w-full h-fit max-h-full"
                />
              </div>
            )}
          </div>

          {/* Right: infos, 1/4 width */}
          <div className="relative w-3/10 flex flex-col text-white h-full overflow-auto items-center text-center justify-center">
            <div className="absolute top-0 right-0">
              <button
                type="button"
                onClick={onClose}
                className="text-white hover:text-gray-300 self-end"
                aria-label={t("close")}
              >
                <Cross2Icon className="h-8 w-8" />
              </button>
            </div>

            <div className="px-4 flex flex-col gap-6 items-start text-left w-full h-fit">
              <h2
                style={{
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#fff",
                  textAlign: "center",
                }}
              >
                {data.estampesMultiplesDescriptionTitre}
                {data.estampesMultiplesDescriptionDateDeCreation && (
                  <span
                    style={{
                      fontWeight: 700,
                      color: "#fff",
                      textAlign: "left",
                    }}
                  >
                    {" "}
                    ({data.estampesMultiplesDescriptionDateDeCreation})
                  </span>
                )}
              </h2>
              {attachment.name && (
                <p className="text-lg font-normal text-white text-left w-full">
                  {t("signleArtworkMainImageName")} : {attachment.name}
                </p>
              )}

              <div className="flex flex-col gap-3 justify-start text-left w-full">
                {data.estampesMultiplesDescriptionNumeroEnregistrement && (
                  <div
                    className="pb-2"
                    style={{
                      fontSize: "18px",
                      fontWeight: 400,
                      color: "#fff",
                      textAlign: "left",
                    }}
                  >
                    {t(
                      "singleMultipleprintFullscreenNumeroEnregistrementWithoutDoubleDots",
                    )}{" "}
                    : {data.estampesMultiplesDescriptionNumeroEnregistrement}
                  </div>
                )}
                {data.estampesMultiplesDescriptionDateDeCreation && (
                  <div
                    className="pb-2"
                    style={{
                      fontSize: "18px",
                      fontWeight: 400,
                      color: "#fff",
                      textAlign: "left",
                    }}
                  >
                    {t("singleMultipleprintFullscreenDateTirage")} :{" "}
                    {data.estampesMultiplesDescriptionDateDeCreation}
                  </div>
                )}
                {(
                  data as {
                    estampesMultiplesDescriptionTypeMultilingual?: Record<
                      string,
                      string
                    >;
                  }
                ).estampesMultiplesDescriptionTypeMultilingual?.[locale] && (
                  <div
                    className="pb-2"
                    style={{
                      fontSize: "18px",
                      fontWeight: 400,
                      color: "#fff",
                      textAlign: "left",
                    }}
                  >
                    {t("singleMultipleprintFullscreenTypeTirage")} :{" "}
                    {(
                      data as {
                        estampesMultiplesDescriptionTypeMultilingual?: Record<
                          string,
                          string
                        >;
                      }
                    ).estampesMultiplesDescriptionTypeMultilingual?.[locale]}
                  </div>
                )}
                {data.estampesMultiplesDescriptionFabricantImprimeur && (
                  <div
                    className="pb-2"
                    style={{
                      fontSize: "18px",
                      fontWeight: 400,
                      color: "#fff",
                      textAlign: "left",
                    }}
                  >
                    {t("estampesMultiplesDescriptionFabricantImprimeur")} :{" "}
                    {data.estampesMultiplesDescriptionFabricantImprimeur}
                  </div>
                )}
                {data.estampesMultiplesDescriptionEditeur && (
                  <div
                    className="pb-2"
                    style={{
                      fontSize: "18px",
                      fontWeight: 400,
                      color: "#fff",
                      textAlign: "left",
                    }}
                  >
                    {t("estampesMultiplesDescriptionEditeur")} :{" "}
                    {data.estampesMultiplesDescriptionEditeur}
                  </div>
                )}
                {supportTechniqueCalculated && (
                  <div
                    className="pb-2"
                    style={{
                      fontSize: "18px",
                      fontWeight: 400,
                      color: "#fff",
                      textAlign: "left",
                    }}
                  >
                    {t("singleMultipleprintFullscreenTechniqueSupport")} :{" "}
                    <span
                      dangerouslySetInnerHTML={{
                        __html: supportTechniqueCalculated,
                      }}
                    />
                  </div>
                )}
                {dimensionsSummaryCalculated && (
                  <div
                    className="pb-2"
                    style={{
                      fontSize: "18px",
                      fontWeight: 400,
                      color: "#fff",
                      textAlign: "left",
                    }}
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: dimensionsSummaryCalculated,
                      }}
                    />
                  </div>
                )}
              </div>
              {footer}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
